<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import CampaignsAPI from 'dashboard/api/campaigns';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
});

const { t, locale } = useI18n();

// "Entregue" e "aceito pela Meta" nao sao a mesma coisa: delivered/read foram confirmados
// pelo WhatsApp, `sent` so quer dizer que a Meta devolveu o wamid e pos na fila. Somar os
// tres num tile de "enviados com sucesso" fazia o CS afirmar entrega sem base (86ajjjghm).
const DELIVERED_STATUSES = ['delivered', 'read'];
const EXPORTABLE_STATUSES = ['failed', 'skipped'];
const STATUS_ORDER = [
  'failed',
  'skipped',
  'pending',
  'sent',
  'delivered',
  'read',
];
const STATUS_CHIP_CLASSES = {
  failed: 'bg-n-ruby-9/10 text-n-ruby-11',
  skipped: 'bg-n-amber-9/10 text-n-amber-11',
  pending: 'bg-n-alpha-2 text-n-slate-11',
  sent: 'bg-n-teal-9/10 text-n-teal-11',
  delivered: 'bg-n-teal-9/10 text-n-teal-11',
  read: 'bg-n-teal-9/10 text-n-teal-11',
};
// Placeholder shown when a value is missing (mapped count / phone). Bound instead of
// inlined so the i18n no-raw-text lint doesn't trip on a literal in the template.
const EMPTY_PLACEHOLDER = '—';
const META_SEPARATOR = '·';

const dialogRef = ref(null);
const report = ref(null);
const errorMessage = ref('');
const isFetching = ref(false);
const isExporting = ref(false);
// A Meta confirma entrega/falha por webhook minutos (as vezes horas) depois do disparo, entao
// o relatorio e um retrato do instante em que foi carregado. Sem dizer QUANDO foi carregado,
// o CS nao sabe se esta olhando o resultado final (86ajjjghm).
const lastFetchedAt = ref(null);
// Monotonic token: the dialog is reused across campaigns, so a slow response from a
// previously-opened campaign must not overwrite the one the user is looking at now.
let fetchToken = 0;

const summary = computed(() => report.value?.summary ?? {});
const contacts = computed(() => report.value?.contacts ?? []);

const countOf = statuses =>
  statuses.reduce((total, status) => total + (summary.value[status] ?? 0), 0);

const mappedCount = computed(() => summary.value.mapped);
const hasMappedCount = computed(() => Number.isFinite(mappedCount.value));
const deliveredCount = computed(() => countOf(DELIVERED_STATUSES));
const awaitingCount = computed(() => summary.value.sent ?? 0);
const failedCount = computed(() => summary.value.failed ?? 0);
const skippedCount = computed(() => summary.value.skipped ?? 0);
// `pending` = a mensagem existe no Helevor mas a Meta nunca devolveu o wamid. Nao entrava em
// contador nenhum, entao "Mapeados" nao fechava com a soma dos cards e ninguem explicava por que.
const pendingCount = computed(() => summary.value.pending ?? 0);
const hasPendingContacts = computed(() => pendingCount.value > 0);
const hasExportableContacts = computed(() => countOf(EXPORTABLE_STATUSES) > 0);

const dateTimeFormatter = computed(
  () =>
    new Intl.DateTimeFormat(locale.value?.replace('_', '-') || undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    })
);

const formatDateTime = value => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : dateTimeFormatter.value.format(date);
};

const dispatchedAt = computed(() =>
  formatDateTime(report.value?.finished_at || report.value?.started_at)
);
const refreshedAt = computed(() => formatDateTime(lastFetchedAt.value));

const statusWeight = status => {
  const index = STATUS_ORDER.indexOf(status);
  return index === -1 ? STATUS_ORDER.length : index;
};

const sortedContacts = computed(() =>
  [...contacts.value].sort(
    (first, second) => statusWeight(first.status) - statusWeight(second.status)
  )
);

const hasContacts = computed(() => sortedContacts.value.length > 0);

// Static i18n keys per status (no dynamic key lookup, which the i18n lint flags).
const statusLabels = computed(() => ({
  failed: t('CAMPAIGN.WHATSAPP.REPORT.STATUS.FAILED'),
  skipped: t('CAMPAIGN.WHATSAPP.REPORT.STATUS.SKIPPED'),
  pending: t('CAMPAIGN.WHATSAPP.REPORT.STATUS.PENDING'),
  sent: t('CAMPAIGN.WHATSAPP.REPORT.STATUS.SENT'),
  delivered: t('CAMPAIGN.WHATSAPP.REPORT.STATUS.DELIVERED'),
  read: t('CAMPAIGN.WHATSAPP.REPORT.STATUS.READ'),
}));

const statusLabel = status => statusLabels.value[status] ?? status;

const statusChipClass = status =>
  STATUS_CHIP_CLASSES[status] ?? STATUS_CHIP_CLASSES.pending;

// Categoria da falha: responde "isso é erro nosso ou decisão do WhatsApp?", que é a primeira
// pergunta do CS ao abrir o relatório. Chaves estáticas (o lint de i18n rejeita chave dinâmica).
const categoryLabels = computed(() => ({
  meta_policy: t('CAMPAIGN.WHATSAPP.REPORT.CATEGORY.META_POLICY'),
  contact: t('CAMPAIGN.WHATSAPP.REPORT.CATEGORY.CONTACT'),
  config: t('CAMPAIGN.WHATSAPP.REPORT.CATEGORY.CONFIG'),
  provider: t('CAMPAIGN.WHATSAPP.REPORT.CATEGORY.PROVIDER'),
}));

// Sem verbete no catálogo o backend devolve `unknown` e o motivo cru da Meta: nesse caso não
// rotulamos nada, para não afirmar uma categoria que não conhecemos.
const categoryLabel = category => categoryLabels.value[category] ?? '';

// parseAPIErrorResponse returns the raw error when the API sends no message,
// so we only trust it when it resolves to a string; the caller passes an already
// translated fallback so t() is never called with a dynamic key.
const apiErrorMessage = (error, fallback) => {
  const parsedError = parseAPIErrorResponse(error);
  return typeof parsedError === 'string' && parsedError
    ? parsedError
    : fallback;
};

const fetchReport = async () => {
  const campaignId = props.campaign?.id;
  if (!campaignId) return;

  fetchToken += 1;
  const token = fetchToken;
  isFetching.value = true;
  errorMessage.value = '';
  report.value = null;

  try {
    const { data } = await CampaignsAPI.report(campaignId);
    if (token !== fetchToken) return; // a newer open() superseded this request
    report.value = data;
    lastFetchedAt.value = new Date();
  } catch (error) {
    if (token !== fetchToken) return;
    errorMessage.value = apiErrorMessage(
      error,
      t('CAMPAIGN.WHATSAPP.REPORT.API.ERROR_MESSAGE')
    );
  } finally {
    if (token === fetchToken) isFetching.value = false;
  }
};

const handleExport = async () => {
  const campaignId = props.campaign?.id;
  if (!campaignId) return;

  isExporting.value = true;

  try {
    const { data } = await CampaignsAPI.failedContacts(campaignId);
    downloadCsvFile(`disparo-${campaignId}-falhas.csv`, data);
  } catch (error) {
    useAlert(
      apiErrorMessage(error, t('CAMPAIGN.WHATSAPP.REPORT.EXPORT.ERROR_MESSAGE'))
    );
  } finally {
    isExporting.value = false;
  }
};

const open = async () => {
  dialogRef.value?.open();
  await fetchReport();
};

const close = () => dialogRef.value?.close();

defineExpose({ dialogRef, open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="2xl"
    :title="t('CAMPAIGN.WHATSAPP.REPORT.TITLE')"
    :description="campaign?.title || ''"
  >
    <div
      v-if="isFetching"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <div
      v-else-if="errorMessage"
      class="flex flex-col items-start gap-3 p-4 rounded-lg bg-n-alpha-2"
    >
      <p class="mb-0 text-sm text-n-slate-11">
        {{ errorMessage }}
      </p>
      <Button
        variant="faded"
        color="slate"
        size="sm"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.REPORT.RETRY')"
        @click="fetchReport"
      />
    </div>

    <div v-else-if="report" class="flex flex-col gap-4">
      <div
        class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="flex flex-col gap-0.5">
          <span v-if="dispatchedAt" class="text-xs text-n-slate-11">
            {{
              t('CAMPAIGN.WHATSAPP.REPORT.DISPATCHED_AT', { at: dispatchedAt })
            }}
          </span>
          <span v-if="refreshedAt" class="text-xs text-n-slate-10">
            {{
              t('CAMPAIGN.WHATSAPP.REPORT.REFRESHED_AT', { at: refreshedAt })
            }}
          </span>
        </div>
        <Button
          variant="faded"
          color="slate"
          size="sm"
          type="button"
          icon="i-lucide-refresh-cw"
          class="self-start sm:self-auto"
          :label="t('CAMPAIGN.WHATSAPP.REPORT.REFRESH')"
          :disabled="isFetching"
          @click="fetchReport"
        />
      </div>

      <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <div class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-2">
          <span class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.REPORT.STATS.MAPPED') }}
          </span>
          <span class="text-xl font-medium text-n-slate-12">
            {{ hasMappedCount ? mappedCount : EMPTY_PLACEHOLDER }}
          </span>
        </div>
        <div class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-2">
          <span class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.REPORT.STATS.DELIVERED') }}
          </span>
          <span class="text-xl font-medium text-n-teal-11">
            {{ deliveredCount }}
          </span>
        </div>
        <div class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-2">
          <span class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.REPORT.STATS.AWAITING') }}
          </span>
          <span class="text-xl font-medium text-n-slate-12">
            {{ awaitingCount }}
          </span>
        </div>
        <div class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-2">
          <span class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.REPORT.STATS.FAILED') }}
          </span>
          <span class="text-xl font-medium text-n-ruby-11">
            {{ failedCount }}
          </span>
        </div>
        <div class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-2">
          <span class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.REPORT.STATS.SKIPPED') }}
          </span>
          <span class="text-xl font-medium text-n-amber-11">
            {{ skippedCount }}
          </span>
        </div>
      </div>

      <p class="mb-0 text-xs text-n-slate-10">
        {{ t('CAMPAIGN.WHATSAPP.REPORT.DELIVERY_HINT') }}
      </p>

      <p v-if="hasPendingContacts" class="mb-0 text-xs text-n-amber-11">
        {{
          t('CAMPAIGN.WHATSAPP.REPORT.PENDING_HINT', { count: pendingCount })
        }}
      </p>

      <p v-if="!hasMappedCount" class="mb-0 text-xs text-n-slate-10">
        {{ t('CAMPAIGN.WHATSAPP.REPORT.LEGACY_HINT') }}
      </p>

      <div
        v-if="hasContacts"
        class="flex flex-col gap-2 pr-1 overflow-y-auto max-h-[45vh]"
      >
        <div
          v-for="(contact, index) in sortedContacts"
          :key="`${contact.contact_id}-${index}`"
          class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-2"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="flex flex-col min-w-0 gap-0.5">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ contact.name || t('CAMPAIGN.WHATSAPP.REPORT.NO_NAME') }}
              </span>
              <span class="text-xs truncate text-n-slate-11">
                {{ contact.phone_number || contact.email || EMPTY_PLACEHOLDER }}
              </span>
            </div>
            <span
              class="flex-shrink-0 text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md"
              :class="statusChipClass(contact.status)"
            >
              {{ statusLabel(contact.status) }}
            </span>
          </div>
          <p
            v-if="contact.error"
            class="mb-0 text-xs break-words text-n-slate-11"
          >
            <span class="font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP.REPORT.REASON') }}
            </span>
            {{ contact.error }}
          </p>
          <p
            v-if="contact.error_action"
            class="mb-0 text-xs break-words text-n-slate-11"
          >
            <span class="font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP.REPORT.ACTION') }}
            </span>
            {{ contact.error_action }}
          </p>
          <p
            v-if="categoryLabel(contact.error_category) || contact.error_code"
            class="mb-0 text-xs text-n-slate-10"
          >
            <span v-if="categoryLabel(contact.error_category)">
              {{ categoryLabel(contact.error_category) }}
            </span>
            <span
              v-if="categoryLabel(contact.error_category) && contact.error_code"
            >
              {{ META_SEPARATOR }}
            </span>
            <span v-if="contact.error_code">
              {{
                t('CAMPAIGN.WHATSAPP.REPORT.META_CODE', {
                  code: contact.error_code,
                })
              }}
            </span>
          </p>
        </div>
      </div>

      <p v-else class="mb-0 text-sm text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.REPORT.EMPTY') }}
      </p>
    </div>

    <template #footer>
      <div
        class="flex flex-col-reverse w-full gap-3 sm:flex-row sm:items-center sm:justify-between"
      >
        <Button
          variant="faded"
          color="slate"
          type="button"
          class="w-full sm:w-auto"
          :label="t('CAMPAIGN.WHATSAPP.REPORT.CLOSE')"
          @click="close"
        />
        <Button
          color="blue"
          type="button"
          icon="i-lucide-download"
          class="w-full sm:w-auto"
          :label="t('CAMPAIGN.WHATSAPP.REPORT.EXPORT.LABEL')"
          :is-loading="isExporting"
          :disabled="!hasExportableContacts || isFetching || isExporting"
          @click="handleExport"
        />
      </div>
    </template>
  </Dialog>
</template>
